import {
  getMarketingConsent,
  getStoredMarketingConsent,
  type MarketingConsent,
} from './analytics.ts'

export const META_ATTRIBUTION_STORAGE_KEY = 'sg.meta-attribution.v1'
export const META_ATTRIBUTION_MAX_AGE_DAYS = 30

const MAX_AGE_MS = META_ATTRIBUTION_MAX_AGE_DAYS * 24 * 60 * 60 * 1000
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/g

export type MetaAttributionSnapshot = {
  version: 1
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmContent: string | null
  utmTerm: string | null
  fbclid: string | null
  fbc: string | null
  fbp: string | null
  landingPath: string
  capturedAt: string
  expiresAt: string
}

export type CheckoutMeasurementEnvelope = {
  version: 1
  storedMarketingChoice: MarketingConsent
  campaignAttribution: MetaAttributionSnapshot | null
  capturedAt: string
}

function safeStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function cleanBounded(value: unknown, maxLength: number): string | null {
  if (typeof value !== 'string') return null
  const clean = value.replace(CONTROL_CHARACTERS, '').trim()
  if (!clean || clean.length > maxLength) return null
  return clean
}

export function sanitizeMetaCampaignValue(value: unknown): string | null {
  return cleanBounded(value, 160)
}

export function sanitizeMetaClickId(value: unknown): string | null {
  return cleanBounded(value, 500)
}

export function sanitizeMetaBrowserIdentifier(value: unknown): string | null {
  const clean = cleanBounded(value, 512)
  if (!clean || !clean.startsWith('fb.')) return null
  return /^[A-Za-z0-9._:-]+$/.test(clean) ? clean : null
}

export function sanitizeMetaLandingPath(value: unknown): string | null {
  const clean = cleanBounded(value, 512)
  if (!clean || !clean.startsWith('/') || clean.startsWith('//')) return null
  return clean
}

function safeIso(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : null
}

function readCookie(name: '_fbp' | '_fbc'): string | null {
  if (typeof document === 'undefined') return null
  const encoded = `${name}=`
  const raw = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(encoded))
  if (!raw) return null
  try {
    return decodeURIComponent(raw.slice(encoded.length))
  } catch {
    return raw.slice(encoded.length)
  }
}

export function normaliseMetaAttributionSnapshot(
  value: unknown,
  nowMs = Date.now(),
): MetaAttributionSnapshot | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  if (record.version !== 1) return null

  const capturedAt = safeIso(record.capturedAt)
  const expiresAt = safeIso(record.expiresAt)
  const landingPath = sanitizeMetaLandingPath(record.landingPath)
  if (!capturedAt || !expiresAt || !landingPath) return null

  const capturedMs = Date.parse(capturedAt)
  const expiresMs = Date.parse(expiresAt)
  if (capturedMs > nowMs + 5 * 60 * 1000 || expiresMs <= nowMs || expiresMs - capturedMs > MAX_AGE_MS + 60_000) return null

  return {
    version: 1,
    utmSource: sanitizeMetaCampaignValue(record.utmSource),
    utmMedium: sanitizeMetaCampaignValue(record.utmMedium),
    utmCampaign: sanitizeMetaCampaignValue(record.utmCampaign),
    utmContent: sanitizeMetaCampaignValue(record.utmContent),
    utmTerm: sanitizeMetaCampaignValue(record.utmTerm),
    fbclid: sanitizeMetaClickId(record.fbclid),
    fbc: sanitizeMetaBrowserIdentifier(record.fbc),
    fbp: sanitizeMetaBrowserIdentifier(record.fbp),
    landingPath,
    capturedAt,
    expiresAt,
  }
}

function readStoredSnapshot(nowMs = Date.now()): MetaAttributionSnapshot | null {
  const storage = safeStorage()
  if (!storage) return null
  try {
    const raw = storage.getItem(META_ATTRIBUTION_STORAGE_KEY)
    if (!raw) return null
    const snapshot = normaliseMetaAttributionSnapshot(JSON.parse(raw), nowMs)
    if (!snapshot) storage.removeItem(META_ATTRIBUTION_STORAGE_KEY)
    return snapshot
  } catch {
    return null
  }
}

function writeSnapshot(snapshot: MetaAttributionSnapshot): void {
  const storage = safeStorage()
  if (!storage) return
  try {
    storage.setItem(META_ATTRIBUTION_STORAGE_KEY, JSON.stringify(snapshot))
  } catch {
    // Attribution persistence is optional and must never affect checkout.
  }
}

export function clearMetaAttribution(): void {
  const storage = safeStorage()
  if (!storage) return
  try {
    storage.removeItem(META_ATTRIBUTION_STORAGE_KEY)
  } catch {
    // Best effort only.
  }
}

function currentCookieIdentifiers() {
  return {
    fbc: sanitizeMetaBrowserIdentifier(readCookie('_fbc')),
    fbp: sanitizeMetaBrowserIdentifier(readCookie('_fbp')),
  }
}

export function captureMetaAttributionFromCurrentLocation(nowMs = Date.now()): MetaAttributionSnapshot | null {
  if (typeof window === 'undefined' || getMarketingConsent() !== 'granted') return null

  const url = new URL(window.location.href)
  const current = readStoredSnapshot(nowMs)
  const cookies = currentCookieIdentifiers()
  const candidate = {
    utmSource: sanitizeMetaCampaignValue(url.searchParams.get('utm_source')),
    utmMedium: sanitizeMetaCampaignValue(url.searchParams.get('utm_medium')),
    utmCampaign: sanitizeMetaCampaignValue(url.searchParams.get('utm_campaign')),
    utmContent: sanitizeMetaCampaignValue(url.searchParams.get('utm_content')),
    utmTerm: sanitizeMetaCampaignValue(url.searchParams.get('utm_term')),
    fbclid: sanitizeMetaClickId(url.searchParams.get('fbclid')),
    fbc: cookies.fbc,
    fbp: cookies.fbp,
  }
  const hasFreshUrlTouch = Boolean(
    candidate.utmSource
    || candidate.utmMedium
    || candidate.utmCampaign
    || candidate.utmContent
    || candidate.utmTerm
    || candidate.fbclid
  )

  // Persistent Meta cookies alone do not make a later direct page view a new
  // campaign touch. Keep the existing campaign fields and only refresh missing
  // browser identifiers.
  if (current && !hasFreshUrlTouch) {
    const merged = {
      ...current,
      fbc: current.fbc ?? candidate.fbc,
      fbp: current.fbp ?? candidate.fbp,
    }
    if (merged.fbc !== current.fbc || merged.fbp !== current.fbp) writeSnapshot(merged)
    return merged
  }

  if (!hasFreshUrlTouch && !candidate.fbc && !candidate.fbp) return current

  const snapshot: MetaAttributionSnapshot = {
    version: 1,
    ...candidate,
    landingPath: sanitizeMetaLandingPath(url.pathname) ?? '/',
    capturedAt: new Date(nowMs).toISOString(),
    expiresAt: new Date(nowMs + MAX_AGE_MS).toISOString(),
  }
  writeSnapshot(snapshot)
  return snapshot
}

export function getMetaAttributionSnapshot(nowMs = Date.now()): MetaAttributionSnapshot | null {
  if (typeof window === 'undefined' || getMarketingConsent() !== 'granted') return null
  const snapshot = readStoredSnapshot(nowMs) ?? captureMetaAttributionFromCurrentLocation(nowMs)
  if (!snapshot) return null

  const cookies = currentCookieIdentifiers()
  const merged = {
    ...snapshot,
    fbc: cookies.fbc ?? snapshot.fbc,
    fbp: cookies.fbp ?? snapshot.fbp,
  }
  if (merged.fbc !== snapshot.fbc || merged.fbp !== snapshot.fbp) writeSnapshot(merged)
  return merged
}

export function buildCheckoutMeasurementEnvelope(nowMs = Date.now()): CheckoutMeasurementEnvelope {
  const storedMarketingChoice = getStoredMarketingConsent()
  const permitted = getMarketingConsent() === 'granted'
  return {
    version: 1,
    storedMarketingChoice,
    campaignAttribution: permitted ? getMetaAttributionSnapshot(nowMs) : null,
    capturedAt: new Date(nowMs).toISOString(),
  }
}
